import { Flex, getChainIconByChainId, MPROToken, Text, TextVariant } from '@mprolab/uikit'
import { Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import * as React from 'react'
import { RefreshCcw } from 'react-feather'
import styled, { useTheme } from 'styled-components'
import UseAnimations from 'react-useanimations'
import loading from 'react-useanimations/lib/loading'
import useScreenSize from 'hooks/useScreenSize'
import isNil from 'lodash/isNil'
import { getNativeCoinTickerByChainId } from '@mprolab/sdk'

const TickerWrapper = styled(Flex)`
  align-items: center;
  gap: 8px;
  width: max-content;
  border-radius: 24px;
  padding: 8px 13px 8px 8px;
  background: ${({ theme }) => theme.colors.gray10};
`

const GreenBorderWrapper = styled(Flex)`
  gap: 8px;
  padding: 2px 12px;
  align-items: center;
  border-radius: 32px;
  width: max-content;
  background: rgba(109, 232, 51, 0.3);
  border: 1px solid ${({ theme }) => theme.colors.green};
`

const CyanBorderWrapper = styled(Flex)`
  gap: 8px;
  padding: 2px 12px;
  align-items: center;
  border-radius: 32px;
  width: max-content;
  background: rgba(3, 245, 255, 0.3);
  border: 1px solid ${({ theme }) => theme.colors.cyan};
`

const Column = styled(Flex)`
  flex-direction: column;
  gap: 8px;
`

const StyledRefresh = styled(RefreshCcw)`
  -webkit-transform: scaleX(-1);
  transform: scaleX(-1);
`

interface TableContentProps {
  apy: number
  fee: number
  isLoading: boolean
  chainId: number
  isDual: boolean
}

const TokenTicker: React.FC<{ ticker: string; chainId?: number }> = ({ ticker, chainId }) => {
  const icon = ticker === 'MPRO' ? <MPROToken /> : getChainIconByChainId(chainId, '24px', '24px')
  return (
    <TickerWrapper width="150px">
      <Flex>{icon}</Flex>
      <Text variant={TextVariant.BODY_SMALL_BOLD}>{ticker}</Text>
    </TickerWrapper>
  )
}

const TableContent: React.FC<TableContentProps> = ({ apy, fee, isLoading, chainId, isDual }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { isMobile } = useScreenSize()
  const TOKEN_TICKER = 'MPRO'
  const NATIVE_TICKER = getNativeCoinTickerByChainId(chainId)

  const AmountContainer = ({ value, isLoading }: { value: number; isLoading: boolean }) => {
    return (
      <Grid item xs={1}>
        {isLoading ? (
          <UseAnimations animation={loading} strokeColor={colors.gray6} />
        ) : (
          <Text variant={TextVariant.BODY_SMALL}>{value ? `${value}%` : '-'}</Text>
        )}
      </Grid>
    )
  }

  const AmountContainerMobile = ({ value, title, isLoading }: { value: number; title: string; isLoading: boolean }) => {
    return (
      <Column width="150px">
        <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
          {title}
        </Text>
        {isLoading ? (
          <UseAnimations animation={loading} strokeColor={colors.gray6} />
        ) : (
          <Text variant={TextVariant.BODY_BIG_BOLD} verticalAlign="center">
            {!isNil(value) ? `${value}%` : '-'}
          </Text>
        )}
      </Column>
    )
  }

  const AutoCell = () => {
    return (
      <GreenBorderWrapper>
        <Text variant={TextVariant.BODY_SMALL}>{t('stakingPanel.auto')}</Text>
      </GreenBorderWrapper>
    )
  }

  const AutoCompoundCell = () => {
    return (
      <CyanBorderWrapper>
        <StyledRefresh color={colors.white} size={16} />
        <Text variant={TextVariant.BODY_SMALL} style={{ whiteSpace: 'nowrap' }}>
          {t('stakingPanel.autoCompound')}
        </Text>
      </CyanBorderWrapper>
    )
  }

  const mobileView = (
    <Flex flexDirection="column" gap="16px">
      <Column>
        <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
          {t('stakingPanel.stake')}
        </Text>
        <Flex gap="16px">
          <AutoCell />
          <AutoCompoundCell />
        </Flex>
      </Column>
      <Flex>
        <Column width="150px">
          <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
            {t('stakingPanel.earn')}
          </Text>
          <TokenTicker ticker={TOKEN_TICKER} />
        </Column>
        <Column width="150px">
          <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
            {t('stakingPanel.token')}
          </Text>
          <TokenTicker ticker={isDual ? NATIVE_TICKER : TOKEN_TICKER} chainId={chainId} />
        </Column>
      </Flex>
      <Flex>
        <AmountContainerMobile title={t('stakingPanel.apy')} value={apy} {...{ isLoading }} />
        <AmountContainerMobile title={t('stakingPanel.fee')} value={fee} {...{ isLoading }} />
      </Flex>
    </Flex>
  )

  const desktopView = (
    <>
      <Grid container display="flex" alignItems="center" padding="8px">
        <Grid item xs={1.5}>
          <AutoCell />
        </Grid>
        <Grid item xs={1.5}>
          <TokenTicker ticker={TOKEN_TICKER} />
        </Grid>
        <Grid item xs={5}>
          <TokenTicker ticker={isDual ? NATIVE_TICKER : TOKEN_TICKER} chainId={chainId} />
        </Grid>
        {/* APY Amount */}
        <AmountContainer value={apy} {...{ isLoading }} />
        {/* FEE Amount */}
        <AmountContainer value={fee} {...{ isLoading }} />
        <Grid item xs={2} display="flex" justifyContent="flex-end">
          <AutoCompoundCell />
        </Grid>
      </Grid>
    </>
  )

  return <>{isMobile ? mobileView : desktopView}</>
}

export default TableContent
